import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import WelcomeHeader from './components/WelcomeHeader';
import QuickActions from './components/QuickActions';
import ShipmentHistorySection from './components/ShipmentHistorySection';
import FilterControls from './components/FilterControls';
import StatisticsPanel from './components/StatisticsPanel';


const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [user, setUser] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock user and shipment data
  useEffect(() => {
    const mockUser = localStorage.getItem('currentUser');
    if (mockUser) {
      setUser(JSON.parse(mockUser));
    } else {
      // Set default user if not logged in
      setUser({
        id: 'user_001',
        name: 'Sarah Johnson',
        email: 'sarah.johnson@email.com',
        accountStatus: 'Premium'
      });
    }

    const mockCartCount = localStorage.getItem('cartCount');
    if (mockCartCount) {
      setCartCount(parseInt(mockCartCount, 10));
    }

    // Mock shipment data
    const mockShipments = [
      {
        id: 'SHP001',
        trackingNumber: 'DWS1234567890',
        recipient: 'John Smith',
        destination: '456 Market St, San Francisco, CA 94102',
        origin: '123 Main St, New York, NY 10001',
        shippingDate: '2024-01-25T10:00:00Z',
        deliveryDate: '2024-01-28T14:30:00Z',
        estimatedDelivery: '2024-01-28T18:00:00Z',
        status: 'Delivered',
        packageContents: 'Electronics - Laptop',
        weight: '5.2 lbs',
        dimensions: '16x12x3 inches',
        cost: 45.99,
        serviceType: 'Express Shipping'
      },
      {
        id: 'SHP002',
        trackingNumber: 'DWS0987654321',
        recipient: 'Emily Davis',
        destination: '789 Oak Ave, Austin, TX 78701',
        origin: '123 Main St, New York, NY 10001',
        shippingDate: '2024-01-27T09:00:00Z',
        deliveryDate: null,
        estimatedDelivery: '2024-01-30T17:00:00Z',
        status: 'In Transit',
        packageContents: 'Books - Educational Materials',
        weight: '3.8 lbs',
        dimensions: '12x10x4 inches',
        cost: 32.50,
        serviceType: 'Standard Shipping'
      },
      {
        id: 'SHP003',
        trackingNumber: 'DWS5647382910',
        recipient: 'Michael Brown',
        destination: '321 Pine Rd, Seattle, WA 98101',
        origin: '123 Main St, New York, NY 10001',
        shippingDate: '2024-01-28T11:30:00Z',
        deliveryDate: null,
        estimatedDelivery: '2024-01-31T16:00:00Z',
        status: 'Out for Delivery',
        packageContents: 'Clothing - Winter Apparel',
        weight: '2.5 lbs',
        dimensions: '14x10x6 inches',
        cost: 28.75,
        serviceType: 'Express Shipping'
      },
      {
        id: 'SHP004',
        trackingNumber: 'DWS1928374650',
        recipient: 'Lisa Anderson',
        destination: '555 Elm St, Boston, MA 02101',
        origin: '123 Main St, New York, NY 10001',
        shippingDate: '2024-01-29T08:00:00Z',
        deliveryDate: null,
        estimatedDelivery: '2024-02-01T15:00:00Z',
        status: 'Pending',
        packageContents: 'Home Goods - Kitchen Items',
        weight: '7.1 lbs',
        dimensions: '18x14x8 inches',
        cost: 52.30,
        serviceType: 'Standard Shipping'
      },
      {
        id: 'SHP005',
        trackingNumber: 'DWS8765432109',
        recipient: 'David Wilson',
        destination: '888 Maple Dr, Chicago, IL 60601',
        origin: '123 Main St, New York, NY 10001',
        shippingDate: '2024-01-20T13:00:00Z',
        deliveryDate: '2024-01-23T11:45:00Z',
        estimatedDelivery: '2024-01-23T18:00:00Z',
        status: 'Delivered',
        packageContents: 'Sports Equipment - Tennis Racket',
        weight: '4.3 lbs',
        dimensions: '28x12x4 inches',
        cost: 38.90,
        serviceType: 'Express Shipping'
      },
      {
        id: 'SHP006',
        trackingNumber: 'DWS3456789012',
        recipient: 'Jennifer Martinez',
        destination: '999 Cedar Ln, Miami, FL 33101',
        origin: '123 Main St, New York, NY 10001',
        shippingDate: '2024-01-15T10:30:00Z',
        deliveryDate: '2024-01-18T16:20:00Z',
        estimatedDelivery: '2024-01-18T18:00:00Z',
        status: 'Delivered',
        packageContents: 'Beauty Products - Cosmetics Set',
        weight: '1.8 lbs',
        dimensions: '10x8x4 inches',
        cost: 25.60,
        serviceType: 'Standard Shipping'
      }
    ];

    setShipments(mockShipments);
    setFilteredShipments(mockShipments);
  }, []);

  // Filter shipments based on status, date, and search
  useEffect(() => {
    let filtered = [...shipments];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(shipment => 
        shipment?.status?.toLowerCase() === statusFilter?.toLowerCase()
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered?.filter(shipment => {
        const shipDate = new Date(shipment?.shippingDate);
        const daysDiff = Math.floor((now - shipDate) / (1000 * 60 * 60 * 24));

        switch (dateFilter) {
          case 'week':
            return daysDiff <= 7;
          case 'month':
            return daysDiff <= 30;
          case '3months':
            return daysDiff <= 90;
          default:
            return true;
        }
      });
    }

    // Search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase()?.trim();
      filtered = filtered?.filter(shipment => 
        shipment?.trackingNumber?.toLowerCase()?.includes(query) ||
        shipment?.recipient?.toLowerCase()?.includes(query) ||
        shipment?.destination?.toLowerCase()?.includes(query) ||
        shipment?.packageContents?.toLowerCase()?.includes(query)
      );
    }

    setFilteredShipments(filtered);
  }, [statusFilter, dateFilter, searchQuery, shipments]);

  const handleReorder = (shipment) => {
    // Pre-populate shipping form with shipment details
    localStorage.setItem('reorderShipment', JSON.stringify({
      recipient: shipment?.recipient,
      destination: shipment?.destination,
      packageContents: shipment?.packageContents,
      weight: shipment?.weight,
      dimensions: shipment?.dimensions,
      serviceType: shipment?.serviceType
    }));
    navigate('/table-reservation'); // Navigate to shipment booking page
  };

  const handleViewDetails = (shipment) => {
    navigate('/shipment-tracking', { state: { trackingNumber: shipment?.trackingNumber } });
  };

  const handleNewShipment = () => {
    navigate('/table-reservation');
  };

  const handleTrackShipment = () => {
    navigate('/shipment-tracking');
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setUser(null);
    navigate('/login');
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = (action) => {
    if (action === 'login') {
      navigate('/login');
    } else if (action === 'register') {
      navigate('/register');
    } else if (action === 'account') {
      navigate('/user-account');
    }
  };

  // Add this handler function
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        cartCount={cartCount}
        user={user}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onLogout={handleLogout}
        onSearch={handleSearch}
      />

      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Welcome Header */}
        <WelcomeHeader user={user} />

        {/* Quick Actions */}
        <QuickActions
          onNewShipment={handleNewShipment}
          onTrackShipment={handleTrackShipment}
        />

        {/* Statistics Panel */}
        <StatisticsPanel shipments={shipments} />

        {/* Filter Controls */}
        <FilterControls
          statusFilter={statusFilter}
          dateFilter={dateFilter}
          searchQuery={searchQuery}
          onStatusChange={setStatusFilter}
          onDateChange={setDateFilter}
          onSearchChange={setSearchQuery}
        />

        {/* Shipment History */}
        <ShipmentHistorySection
          shipments={filteredShipments}
          onReorder={handleReorder}
          onViewDetails={handleViewDetails}
        />
      </main>
    </div>
  );
};

export default CustomerDashboard;